import { useState, useEffect, useRef } from 'react';
import v2Api from './v2Api';

// Regex to find {#image URL} patterns
const IMAGE_REGEX = /\{#image\s+(https?:\/\/[^}]+)\}/g;

// Module-level cache so signed URLs persist across re-renders and components
const signedUrlCache = {};

/**
 * Hook that takes content strings, finds S3 image URLs in {#image ...} syntax,
 * batch-signs them via the API, and returns the content with signed URLs.
 *
 * @param {string|string[]} contents - One or more content strings to process
 * @returns {string|string[]} - Content with S3 URLs replaced by presigned URLs
 */
export default function useSignedContent(contents) {
    const isArray = Array.isArray(contents);
    const items = isArray ? contents : [contents];

    const [signedItems, setSignedItems] = useState(items);
    const pendingRef = useRef(null);

    useEffect(() => {
        // Collect all unique S3 URLs that need signing
        const urlsToSign = new Set();
        for (const content of items) {
            if (!content) continue;
            let match;
            const re = new RegExp(IMAGE_REGEX.source, 'g');
            while ((match = re.exec(content)) !== null) {
                const url = match[1];
                if (url.includes('.s3.') && url.includes('amazonaws.com') && !signedUrlCache[url]) {
                    urlsToSign.add(url);
                }
            }
        }

        if (urlsToSign.size === 0) {
            // All URLs already cached or no S3 URLs — just replace from cache
            setSignedItems(items.map(replaceFromCache));
            return;
        }

        // Batch sign
        const urlArray = [...urlsToSign];
        const requestId = Date.now();
        pendingRef.current = requestId;

        v2Api.post('/tests/sign-urls', { urls: urlArray })
            .then((res) => {
                if (pendingRef.current !== requestId) return; // stale
                const signed = res.data?.data || {};
                // Populate cache
                for (const [orig, presigned] of Object.entries(signed)) {
                    if (presigned) signedUrlCache[orig] = presigned;
                }
                setSignedItems(items.map(replaceFromCache));
            })
            .catch((err) => {
                console.error('[useSignedContent] sign-urls failed:', err);
                // Fall back to original content
                setSignedItems(items.map(replaceFromCache));
            });

        return () => { pendingRef.current = null; };
    }, [items.join('|||')]); // re-run when content changes

    return isArray ? signedItems : signedItems[0];
}

function replaceFromCache(content) {
    if (!content) return content;
    return content.replace(new RegExp(IMAGE_REGEX.source, 'g'), (full, url) => {
        const signed = signedUrlCache[url];
        return signed ? `{#image ${signed}}` : full;
    });
}
